import { createColumnHelper } from '@tanstack/react-table';
import { FC, useMemo } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import styled from 'styled-components';
import {
  RequiredText,
  formatDateTime,
  DataSubjectCommentDto,
  Table,
  TextEllipsis,
} from '@atlas-frontend/sdk';
import translations from './DataSubjectCommentsTable.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'data-subject-comments-table',
);
addTranslations(translations);
const columnHelper = createColumnHelper<DataSubjectCommentDto>();

export interface DataSubjectCommentsEditTableProps {
  comments: DataSubjectCommentDto[];
}

export const DataSubjectCommentsViewTable: FC<
  DataSubjectCommentsEditTableProps
> = (props) => {
  const { comments } = props;
  const { t } = useTranslation();

  const cols = useMemo(
    () => [
      columnHelper.accessor('createdBy.username', {
        header: t('column.user'),
        meta: {
          width: '20ch',
        },
      }),
      columnHelper.accessor('comments', {
        header: () => <RequiredText>{t('column.comment')}</RequiredText>,
        cell: (props) => (
          <TextEllipsis rows={3}>{props.renderValue()}</TextEllipsis>
        ),
        meta: {
          minWidth: 150,
        },
        sortingFn: 'localeCompareCaseSensitive',
      }),
      columnHelper.accessor((rowData) => formatDateTime(rowData.createdAt), {
        header: t('column.createdAt'),
        cell: (props) => props.renderValue(),
        filterFn: 'formattedDate',
        meta: {
          width: 200,
        },
      }),
      columnHelper.accessor((rowData) => formatDateTime(rowData.modifiedAt), {
        id: 'modifiedAt',
        header: t('column.modifiedAt'),
        cell: (props) => props.renderValue(),
        filterFn: 'formattedDate',
        sortingFn: 'formattedDateTime',
        meta: {
          width: 200,
        },
      }),
    ],
    [t],
  );

  return (
    <TableWrapper>
      <Table
        name="comments"
        columns={cols}
        data={comments}
        defaultSort={[{ id: 'modifiedAt', desc: true }]}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </TableWrapper>
  );
};

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
